import React, { useState, useRef, useEffect } from 'react';
import { FaComments, FaTimes, FaPaperPlane, FaRobot } from 'react-icons/fa';
import './Safarbot.css';

const Safarbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: "Hi! I'm Safarbot 🌍 Ask me about Dubai, Sri Lanka, Vietnam, Thailand, Indonesia, Malaysia, Singapore, Laos, or Cambodia!",
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const extractCountry = (message) => {
    // List of supported countries and their variations
    const countries = {
      'dubai': ['dubai', 'uae', 'emirates'],
      'sri lanka': ['sri lanka', 'srilanka', 'lanka'],
      'vietnam': ['vietnam', 'viet nam'],
      'thailand': ['thailand', 'thai'],
      'indonesia': ['indonesia', 'bali'],
      'malaysia': ['malaysia'],
      'singapore': ['singapore'],
      'laos': ['laos'],
      'cambodia': ['cambodia', 'angkor']
    };

    const lowerMessage = message.toLowerCase();
    
    // Check each country and its variations
    for (const [country, variations] of Object.entries(countries)) {
      for (const variation of variations) {
        if (lowerMessage.includes(variation)) {
          return country;
        }
      }
    }
    
    return message; // Return original if no match found
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      type: 'user',
      text: inputMessage,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    // Local destination data as fallback
    const destinationData = {
      'dubai': {
        name: 'Dubai',
        currency: 'UAE Dirham (AED)',
        language: 'Arabic, English',
        bestTime: 'October to April (cooler weather)',
        highlights: [
          'Burj Khalifa - world\'s tallest building',
          'Palm Jumeirah - iconic man-made island',
          'Dubai Mall & Gold Souk - shopping paradise',
          'Desert safari & dune bashing',
          'Marina Bay - stunning skyline views'
        ],
        tips: 'Dress modestly, respect local customs, visit during winter months to avoid extreme heat.'
      },
      'sri-lanka': {
        name: 'Sri Lanka',
        currency: 'Sri Lankan Rupee (LKR)',
        language: 'Sinhala, Tamil, English',
        bestTime: 'December to March (west & south coast), May to September (east coast)',
        highlights: [
          'Sigiriya Rock - ancient fortress on rock pillar',
          'Temple of the Tooth - sacred Buddhist shrine',
          'Tea plantations - lush green landscapes',
          'Beaches & water sports',
          'Wildlife safaris in national parks'
        ],
        tips: 'Learn some Sinhala phrases, try local cuisine, be respectful at temples.'
      },
      'vietnam': {
        name: 'Vietnam',
        currency: 'Vietnamese Dong (VND)',
        language: 'Vietnamese, English (in tourist areas)',
        bestTime: 'October to April (mild and dry)',
        highlights: [
          'Halong Bay - UNESCO limestone karsts',
          'Ho Chi Minh City - vibrant metropolis',
          'Hanoi - historic capital with French architecture',
          'Mekong Delta - floating markets & rice fields',
          'Hoi An - ancient town with lanterns'
        ],
        tips: 'Street food is incredible, bargain at markets, respect war history sites.'
      },
      'thailand': {
        name: 'Thailand',
        currency: 'Thai Baht (THB)',
        language: 'Thai, English (in tourist areas)',
        bestTime: 'November to February (cool and dry)',
        highlights: [
          'Bangkok - bustling capital with temples',
          'Phuket & islands - crystal clear beaches',
          'Chiang Mai - temples and mountain culture',
          'Floating markets - unique shopping experience',
          'Muay Thai - traditional boxing'
        ],
        tips: 'Learn basic Thai phrases, respect the monarchy, remove shoes in temples.'
      },
      'indonesia': {
        name: 'Indonesia',
        currency: 'Indonesian Rupiah (IDR)',
        language: 'Indonesian, Javanese, Balinese, English',
        bestTime: 'April to October (dry season)',
        highlights: [
          'Bali - beaches, temples & rice terraces',
          'Mount Bromo - active volcano',
          'Borobudur Temple - world\'s largest Buddhist temple',
          'Lombok - untouched beaches',
          'Jakarta - capital city blend of old & new'
        ],
        tips: 'Respect Hindu customs in Bali, use left hand for eating, learn about local traditions.'
      },
      'malaysia': {
        name: 'Malaysia',
        currency: 'Malaysian Ringgit (MYR)',
        language: 'Malay, English, Mandarin',
        bestTime: 'December to February (cooler weather)',
        highlights: [
          'Kuala Lumpur - modern skyline with Petronas Towers',
          'Penang - cultural melting pot',
          'Borneo - rainforests & wildlife',
          'Langkawi - tropical island paradise',
          'Cameron Highlands - tea plantations'
        ],
        tips: 'Try local street food, respect Islamic customs, learn about multi-cultural society.'
      },
      'singapore': {
        name: 'Singapore',
        currency: 'Singapore Dollar (SGD)',
        language: 'English, Mandarin, Malay, Tamil',
        bestTime: 'January to March & July to August',
        highlights: [
          'Marina Bay Sands - iconic hotel & skyline',
          'Gardens by the Bay - futuristic gardens',
          'Sentosa Island - entertainment hub',
          'Chinatown & cultural districts',
          'Universal Studios & theme parks'
        ],
        tips: 'Extremely clean & safe, excellent public transport, gum chewing prohibited.'
      },
      'laos': {
        name: 'Laos',
        currency: 'Lao Kip (LAK)',
        language: 'Lao, French, English',
        bestTime: 'October to April (cool & dry)',
        highlights: [
          'Luang Prabang - ancient royal capital',
          'Pha That Luang - national symbol',
          'Mekong River - scenic and relaxing',
          'Buddha Park - quirky sculpture park',
          'Slow pace lifestyle'
        ],
        tips: 'Very laid-back country, learn some Lao words, enjoy the slower pace.'
      },
      'cambodia': {
        name: 'Cambodia',
        currency: 'Cambodian Riel (KHR)',
        language: 'Khmer, French, English',
        bestTime: 'November to February (cool & dry)',
        highlights: [
          'Angkor Wat - massive temple complex',
          'Tonlé Sap Lake - largest lake in SE Asia',
          'Phnom Penh - vibrant capital',
          'Sihanoukville - beach resort town',
          'Silk Island - local crafts & agriculture'
        ],
        tips: 'Respect the history, visit Angkor at sunrise, support local artisans.'
      }
    };

    try {
      // Extract country name from the user's message
      const extractedCountry = extractCountry(inputMessage);
      const countryLower = extractedCountry.toLowerCase().trim();
      
      // Map country variations to standard names
      const countryMap = {
        'dubai': 'dubai',
        'uae': 'dubai',
        'emirates': 'dubai',
        'sri lanka': 'sri-lanka',
        'srilanka': 'sri-lanka',
        'lanka': 'sri-lanka',
        'vietnam': 'vietnam',
        'viet nam': 'vietnam',
        'thailand': 'thailand',
        'thai': 'thailand',
        'indonesia': 'indonesia',
        'bali': 'indonesia',
        'malaysia': 'malaysia',
        'singapore': 'singapore',
        'laos': 'laos',
        'cambodia': 'cambodia',
        'angkor': 'cambodia'
      };

      const normalizedCountry = countryMap[countryLower] || countryLower;
      
      // Check if we have local data for this country
      if (destinationData[normalizedCountry]) {
        const info = destinationData[normalizedCountry];
        const botResponse = {
          type: 'bot',
          text: `
📍 **${info.name}**

💰 Currency: ${info.currency}
🗣️ Language: ${info.language}
🌤️ Best Time to Visit: ${info.bestTime}

✨ **Highlights:**
${info.highlights.map((h) => `• ${h}`).join('\n')}

💡 **Travel Tips:**
${info.tips}
          `.trim(),
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, botResponse]);
      } else {
        const errorMessage = {
          type: 'bot',
          text: "I'm sorry, I couldn't find information about that country. Try asking about Dubai, Sri Lanka, Vietnam, Thailand, Indonesia, Malaysia, Singapore, Laos, or Cambodia!",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
    } catch (error) {
      const errorMessage = {
        type: 'bot',
        text: "Oops! I'm having trouble connecting right now. Please try again later.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const quickQuestions = [
    'Tell me about Dubai',
    'Thailand travel tips',
    'Best time to visit Vietnam',
    'What to see in Sri Lanka',
  ];

  const handleQuickQuestion = (question) => {
    setInputMessage(question);
  };

  const renderMessageText = (text) => {
    return text.split('\n').map((line, i) => {
      // Check if line is a heading (starts with **)
      if (line.trim().startsWith('**') && line.trim().endsWith('**')) {
        const content = line.replace(/\*\*/g, '');
        return <strong key={i} className="message-heading">{content}</strong>;
      }
      // Check if line has bold text
      if (line.includes('**')) {
        const parts = line.split('**');
        return (
          <p key={i}>
            {parts.map((part, j) => 
              j % 2 === 1 ? <strong key={j}>{part}</strong> : part
            )}
          </p>
        );
      }
      // Regular line
      return <p key={i}>{line}</p>;
    });
  };

  return (
    <div className="safarbot-container">
      {/* Chat Button */}
      <button
        className={`safarbot-toggle ${isOpen ? 'active' : ''}`}
        onClick={toggleChat}
        aria-label="Toggle Safarbot"
      >
        {isOpen ? <FaTimes /> : <FaComments />}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="safarbot-window">
          {/* Header */}
          <div className="safarbot-header">
            <div className="safarbot-header-content">
              <FaRobot className="safarbot-icon" />
              <div>
                <h3>Safarbot</h3>
                <span className="safarbot-status">Always here to help</span>
              </div>
            </div>
            <button onClick={toggleChat} className="safarbot-close" aria-label="Close chat">
              <FaTimes />
            </button>
          </div>

          {/* Messages */}
          <div className="safarbot-messages">
            {messages.map((message, index) => (
              <div key={index} className={`message message-${message.type}`}>
                <div className="message-content">
                  {renderMessageText(message.text)}
                </div>
                <span className="message-time">
                  {message.timestamp.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            ))}

            {isLoading && (
              <div className="message message-bot">
                <div className="message-content typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          {messages.length === 1 && (
            <div className="safarbot-quick-questions">
              {quickQuestions.map((question, index) => (
                <button
                  key={index}
                  className="quick-question-btn"
                  onClick={() => handleQuickQuestion(question)}
                >
                  {question}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="safarbot-input-container">
            <input
              ref={inputRef}
              type="text"
              className="safarbot-input"
              placeholder="Ask about a destination..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
            />
            <button
              className="safarbot-send"
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading}
              aria-label="Send message"
            >
              <FaPaperPlane />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Safarbot;
